import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined'
import FlightIcon from '@mui/icons-material/Flight'
import {Box, Card, Divider, FormControlLabel, Radio, RadioGroup, Typography} from '@mui/material'
import FailureIcon from 'assets/images/FailureIcon.svg'
import SuccessIcon from 'assets/images/sucessicon.svg'
import {ENDPOINT} from 'config/path'
import {InfoGroup, IssueDetailGroup, OptionGroup} from 'features/ContactUs/components/FormGroup'
import {type ContactLoaderType} from 'features/ContactUs/contactUs.constants'
import {contactUsAction} from 'features/ContactUs/contactUs.slice'
import {ContactCarrierValidation, ContactRouteLoader, ContactUsValidation} from 'features/ContactUs/contactUs.utils'
import {useFormik} from 'formik'
import {Fragment, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {type AppDispatch} from 'redux/store'
import GradientButton from 'shared/GradientButton'
import PrimaryButton from 'shared/PrimaryButton'
import {browserName, fullBrowserVersion} from 'react-device-detect'
import * as yup from 'yup'
import './index.scss'

const ContactForm = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const contactUs = useSelector((state: any) => state.contactUs)
    const [contactWith, setContactWith] = useState<string>('Contact CPS')
    const [resultPage, setResultPage] = useState<boolean>(false)
    const [isSuccess, setIsSuccess] = useState<boolean>(false)
    const securityParams = useSelector((state: any) => state.login.loginDetails?.securityParams)
    const [validations, setValidations] = useState<any>(yup.object({...ContactUsValidation}))
    const [isAuthenticated, setAuthenticated] = useState<boolean>(false)
    const [issueCPSOptions, setIssueCPSOptions] = useState<object[]>([])
    const [carrierOptions, setCarrierOptions] = useState<any[]>([])
    const [phoneCode, setPhoneCode] = useState<string>()
    const [preValues, setPreValues] = useState<any>()

    const faqButtonHandler = () => {
        navigate(ENDPOINT.faqpage)
    }

    const boxOption = (label: string, icon) => (
        <Box className={`option ${contactWith === label && 'option-checked'}`}>
            {icon}
            {label}
        </Box>
    )

    const contactWithHandler = (e) => {
        setContactWith((prev) => {
            if (prev !== e.target.value) {
                void formik.resetForm()
                void formik.setValues(preValues)
            }
            return e.target.value
        })
        if (e.target.value !== 'Contact CPS') {
            setValidations(yup.object({...ContactUsValidation, ...ContactCarrierValidation}))
        } else {
            setValidations(yup.object({...ContactUsValidation}))
        }
    }

    const formik = useFormik({
        initialValues: {
            carrier: '-Select-',
            issue: '-Select-',
            description: '',
            name: '',
            phone: '',
            countryCode: '1:United States',
            email: '',
            attachments: {}
        },
        validationSchema: validations,
        onSubmit: (values: any) => {
            const payload = {
                ...values,
                isAuthenticated,
                browserName,
                fullBrowserVersion,
                userID: isAuthenticated ? securityParams.userID : 'CPSGuest',
                branchCountryCode: isAuthenticated ? securityParams.branchCountryCode : ''
            }
            dispatch(contactUsAction(payload))
                .then(() => {
                    setResultPage(true)
                })
                .catch(() => {
                    setResultPage(true)
                })
        }
    })

    useEffect(() => {
        if (contactUs.error !== null) {
            setIsSuccess(false)
        } else {
            setIsSuccess(true)
        }
    }, [contactUs])

    useEffect(() => {
        ContactRouteLoader()
            .then((data: ContactLoaderType) => {
                const values: any = data.initialValues
                void formik.setValues(values)
                setPreValues(values)
                setAuthenticated(data.isAuthenticated)
                setIssueCPSOptions(data.issueCPSOptions)
                setPhoneCode(data.phoneCode)
                setCarrierOptions(data.carrierOptions)
            })
            .catch(() => {
                // do nothing
            })
    }, [])

    const acknowledgement = (ack) => {
        if (ack) {
            return {
                icon: SuccessIcon,
                color: '#f7fdf8',
                message: 'Thank you!',
                text1: 'We appreciate you reaching out.',
                text2: 'Our team will review your message and get back to you as soon as possible.'
            }
        } else {
            return {
                icon: FailureIcon,
                color: '#edf2f7',
                message: 'We’re sorry',
                text1: 'It looks like something went wrong.',
                text2: 'Your message was not sent.  Please try again later.'
            }
        }
    }

    return (
        <Fragment>
            <Box height={44} />
            {resultPage ? (
                <Card className='contact-form__container card-container'>
                    <Box
                        className='title-container'
                        sx={{
                            backgroundColor: acknowledgement(isSuccess).color
                        }}
                    >
                        <img src={acknowledgement(isSuccess).icon} alt='icon' />
                        <Typography variant='h2'>{acknowledgement(isSuccess).message}</Typography>
                    </Box>
                    <Box className='body-container'>
                        <Typography variant='h4'>{acknowledgement(isSuccess).text1}</Typography>
                        <Typography variant='h6'>{acknowledgement(isSuccess).text2}</Typography>
                        <GradientButton text='Home' onHandleClick={() => navigate(ENDPOINT.homepage)} />
                    </Box>
                </Card>
            ) : (
                <Box className='contact-form__container'>
                    <Typography variant='h4'>Need some help?</Typography>
                    <GradientButton text='Check our FAQs' onHandleClick={faqButtonHandler} />
                    <Box className='or__divider'>
                        <Typography variant='h6'>OR</Typography>
                        <Box />
                    </Box>
                    <form onSubmit={formik.handleSubmit} className='contact-cps-form'>
                        <RadioGroup
                            aria-labelledby='radio-buttons-group-label'
                            defaultValue='cps'
                            name='radio-buttons-group'
                            className='option__container'
                            onChange={contactWithHandler}
                        >
                            <FormControlLabel
                                value='Contact CPS'
                                control={<Radio />}
                                label={boxOption('Contact CPS', <DesktopWindowsOutlinedIcon />)}
                            />
                            <FormControlLabel
                                value='Contact Carrier'
                                control={<Radio />}
                                label={boxOption('Contact Carrier', <FlightIcon />)}
                            />
                        </RadioGroup>
                        <OptionGroup
                            contactWith={contactWith}
                            formik={formik}
                            cpsOptions={issueCPSOptions}
                            carrierOptions={carrierOptions}
                        />
                        <IssueDetailGroup formik={formik} isAuthenticated={isAuthenticated} />
                        <Divider />
                        <InfoGroup formik={formik} isAuthenticated={isAuthenticated} phoneCode={phoneCode} />
                        <Divider />
                        <PrimaryButton
                            text='Send'
                            type='submit'
                            className='contact__primary-btn'
                            isLoading={contactUs.isLoading}
                        />
                    </form>
                </Box>
            )}
            <Box height={81} />
        </Fragment>
    )
}

export default ContactForm
